import * as THREE from "three";
import type { KTX2Loader } from "three/examples/jsm/loaders/KTX2Loader.js";
import { AssetLibrary } from "./assets";
import type { Biome } from "../ui/chapters";
import { BiomeRamps, gradedTexture, type Slot } from "./biomeGrade";
import { CpuRelease } from "./cpuRelease";

/**
 * The ground's tiling maps (GroundMesh delegates its statics here): each biome's texture set, loaded once, shared by
 * URL, cached per biome and released when another biome takes over; the KTX2 twins where the device takes them.
 */
export class GroundMaps {
  /** Set once the tiling maps have loaded; until then the caller keeps the old tiles. */
  static available = false;
  static maps: Maps | null = null;
  /** The loaded ground and road albedos, so the UI can draw road cards from the same textures the map uses. */
  static get albedos(): { grass: THREE.Texture; road: THREE.Texture; dirt: THREE.Texture } | null {
    const m = GroundMaps.maps;
    return m ? { grass: m.grass.alb, road: m.cobble.alb, dirt: m.dirt.alb } : null;
  }
  private static readonly cache = new Map<Biome, Maps>();
  private static readonly loading = new Map<Biome, Promise<Maps | null>>();
  /**
   * Loads the tiling map set for a biome and makes it the one the ground renders with.
   *
   * Each biome ships the same five SLOTS — `grass` (the ground), `dirt` (the worn shoulder), `cobble` (the road),
   * `rock` (the cliff face) and `soil` (the band under the grass lip) — under `hq/terrain/<biome>/`. The meadow's
   * set is every other biome's fallback: a biome without its own albedo gets the meadow's recoloured into its
   * palette (biomeGrade.ts).
   */
  /** The biome whose map set is currently loaded, for debugging and for the headless captures. */
  static currentBiome: Biome = "meadow";

  static async load(biome: Biome = "meadow"): Promise<boolean> {
    GroundMaps.currentBiome = biome;
    const hit = GroundMaps.cache.get(biome);
    if (hit) { GroundMaps.maps = hit; GroundMaps.available = true; return true; }
    let pending = GroundMaps.loading.get(biome);
    if (!pending) { pending = loadMaps(biome); GroundMaps.loading.set(biome, pending); }
    const maps = await pending;
    if (maps) GroundMaps.cache.set(biome, maps);
    // A later load() for another biome may have been asked for while this one was in flight (level 26 tapped, then
    // back out to level 1): only the latest request may swap the maps under the level being built.
    if (GroundMaps.currentBiome !== biome) return maps !== null;
    GroundMaps.maps = maps;
    GroundMaps.available = maps !== null;
    return GroundMaps.available;
  }

  /**
   * Frees every cached biome's maps except `keep`'s (per-level loading, ART_WOW_PASS Phase 3 §9): three biomes of
   * decoded terrain PNGs held at once was ~84 MB. Call after the previous screen's ground is disposed.
   */
  static release(keep: Biome | null): Biome[] {
    const gone: Biome[] = [];
    // Maps are shared by URL across biomes (a biome without its own file borrows the meadow's, biomeGrade.ts), so a
    // texture the kept or current set still uses is left alone.
    const texturesOf = (maps: Maps): THREE.Texture[] => (Object.values(maps) as MapSet[])
      .flatMap((set) => [set.alb, set.nrm, set.hgt, set.emi])
      .flatMap((t) => (t ? [t, gradedFrom.get(t)] : []))
      .filter((t): t is THREE.Texture => !!t);
    const live = new Set<THREE.Texture>();
    for (const [biome, maps] of GroundMaps.cache) if (biome === keep || maps === GroundMaps.maps) for (const t of texturesOf(maps)) live.add(t);
    if (GroundMaps.maps) for (const t of texturesOf(GroundMaps.maps)) live.add(t);
    for (const [biome, maps] of GroundMaps.cache) {
      if (biome === keep || maps === GroundMaps.maps) continue;
      for (const t of new Set(texturesOf(maps))) {
        if (live.has(t)) continue;
        t.dispose();
        const url = textureUrl.get(t);
        if (url) sharedTextures.delete(url);                  // or the decoded image stays referenced from here
      }
      GroundMaps.cache.delete(biome); GroundMaps.loading.delete(biome);
      gone.push(biome);
    }
    return gone;
  }

  /** True when a biome's maps are decoded and ready, so `load` for it costs nothing. */
  static isCached(biome: Biome): boolean { return GroundMaps.cache.has(biome); }

  /**
   * Stops rendering with the current maps so `release` may free them too. Only once no ground built from them is on
   * screen (the caller has disposed it): the next `load` puts a set back before any ground is built.
   */
  static drop(): void {
    GroundMaps.maps = null;
    GroundMaps.available = false;
  }
}

export interface MapSet { alb: THREE.Texture; nrm: THREE.Texture | null; hgt: THREE.Texture | null; emi: THREE.Texture | null; mean: THREE.Color | null }
export interface Maps { grass: MapSet; dirt: MapSet; cobble: MapSet; rock: MapSet; soil: MapSet; gorge: MapSet }

/** The biome whose set stands in for a file another biome's set is missing. */
const FallbackBiome: Biome = "meadow";
/** The optional per-slot maps: height (parallax and crevices) and emissive (molten cracks). */
interface OptionalMaps { readonly hgt: readonly Slot[]; readonly emi: readonly Slot[] }
const AllSlots: readonly Slot[] = ["grass", "dirt", "cobble", "rock", "soil"];
/**
 * Which optional maps each biome's set under `hq/terrain/<biome>/` actually ships (pinned against the art folder by
 * test/groundMaps.test.ts). Add a slot here when its file is added.
 */
export const ShippedOptionalMaps: Readonly<Record<Biome, OptionalMaps>> = {
  meadow: { hgt: AllSlots, emi: [] },
  marsh: { hgt: AllSlots, emi: [] },
  ashlands: { hgt: AllSlots, emi: ["grass", "cobble"] },
};
/**
 * Which of a slot's maps any ground shader samples (the surface, cliff and chasm-floor materials above). A map not
 * listed is never bound, so it is not loaded: roughness is a uniform per layer (no `_rgh` map is read), the worn
 * dirt has no normal, and only the grass and the road have parallax. tools/ktx/encode-terrain.mjs `Unsampled` and
 * vite.config.ts `UnsampledTerrain` mirror this (test/groundMaps.test.ts pins the encoder's side).
 */
export const SampledMaps: Readonly<Record<Slot, { readonly nrm: boolean; readonly hgt: boolean }>> = {
  grass: { nrm: true, hgt: true },
  dirt: { nrm: false, hgt: false },
  cobble: { nrm: true, hgt: true },
  rock: { nrm: true, hgt: false },
  soil: { nrm: true, hgt: false },
};

/** Every terrain map loaded so far (or its failure), by URL. */
const sharedTextures = new Map<string, Promise<THREE.Texture>>();
/** Each loaded texture's URL in `sharedTextures`, so `GroundMesh.release` can drop the entry along with the texture. */
const textureUrl = new WeakMap<THREE.Texture, string>();
/** A graded albedo's source (a borrowed meadow albedo), released with it. */
const gradedFrom = new WeakMap<THREE.Texture, THREE.Texture>();

async function loadMaps(biome: Biome): Promise<Maps | null> {
  const loader = new THREE.TextureLoader();
  // A biome's own set first, then the meadow's — a file missing from the marsh or ashlands set borrows the meadow's
  // instead of dropping levels 21–60 to the tile kit (biomeGrade.ts). Every biome ships a full set today, so the
  // fallback is only ever reached by a failed request; the old loose set in hq/terrain/ is no longer shipped.
  const own = `terrain/${biome}`;
  const dirs = biome === FallbackBiome ? [own] : [own, `terrain/${FallbackBiome}`];
  const ramps = BiomeRamps[biome];
  const compressed = await compressedTerrain();
  const twins = new Set(compressed?.manifest.sets[biome] ?? []);
  // shared by URL, so a biome borrowing the meadow's normals reuses its textures, not a second copy
  const fetchOnce = (url: string, load: () => Promise<THREE.Texture>, srgb: boolean): Promise<THREE.Texture> => {
    let pending = sharedTextures.get(url);
    if (!pending) {
      pending = load().then((t) => {
        t.wrapS = t.wrapT = THREE.RepeatWrapping;
        if (srgb) t.colorSpace = THREE.SRGBColorSpace;
        t.anisotropy = 8;
        textureUrl.set(t, url);
        return t;
      });
      sharedTextures.set(url, pending);
    }
    return pending;
  };
  const one = async (name: string, srgb: boolean): Promise<{ tex: THREE.Texture; own: boolean }> => {
    // The biome's own map as KTX2 where this device transcodes it (a quarter to an eighth of the PNG's GPU memory),
    // the PNG otherwise or if the twin fails. A borrowed map stays PNG: its albedo is regraded from its pixels.
    if (compressed && twins.has(name)) {
      const url = `${KtxTerrainBase}${biome}/${name}.ktx2`;
      try {
        const tex = await fetchOnce(url, () => compressed.loader.loadAsync(AssetLibrary.resolveInlined(url)), srgb);
        // the transcoded mip chain goes once the main canvas has it (cpuRelease.ts): nothing reads a compressed map's
        // pixels (its mean colour comes from the manifest), and a borrowed map is a PNG
        CpuRelease.texture(tex);
        return { tex, own: true };
      } catch (e) { console.warn(`terrain ${biome}/${name}: KTX2 failed, using PNG`, e); }
    }
    let err: unknown = null;
    for (const dir of dirs) {
      const url = `./hq/${dir}/${name}.png`;
      try {
        return { tex: await fetchOnce(url, () => loader.loadAsync(AssetLibrary.resolveInlined(url)), srgb), own: dir === own };
      } catch (e) { err = e; }
    }
    throw err;
  };
  const tex = async (name: string, srgb: boolean) => (await one(name, srgb)).tex;
  // an optional map is asked for only where the set ships it: each miss was a 404 on every level load (ten a meadow)
  const optional = (k: Slot, kind: keyof OptionalMaps) =>
    (ShippedOptionalMaps[biome][kind].includes(k) ? tex(`${k}_${kind}`, false).catch(() => null) : Promise.resolve(null));
  // a borrowed meadow albedo is recoloured into this biome's palette (biomeGrade.ts); normals carry over as they
  // are. A biome that ships its own set (all three do today) uses it untouched.
  const albedo = (got: { tex: THREE.Texture; own: boolean }, slot: Slot): THREE.Texture => {
    if (got.own || !ramps) return got.tex;
    const g = gradedTexture(got.tex, ramps[slot]);
    if (g !== got.tex) gradedFrom.set(g, got.tex);
    return g;
  };
  // Every file in parallel: done one at a time, the misses (a biome dir without its own maps, the height and emissive
  // maps most sets lack) cost a sequential round trip each — around sixty on a cold load.
  const set = async (k: Slot): Promise<MapSet> => {
    const [alb, nrm, hgt, emi] = await Promise.all([
      one(`${k}_alb`, true),
      SampledMaps[k].nrm ? tex(`${k}_nrm`, false) : Promise.resolve(null),
      // Not every set ships a height map. Without one the shader turns parallax and the crevice term OFF (a neutral
      // height) — it used to march through the roughness map instead, which is a constant for most sets.
      SampledMaps[k].hgt ? optional(k, "hgt") : Promise.resolve(null),
      // only the ashlands ship these: the mask of the cracks that are still molten, so they light rather than just
      // being painted orange
      optional(k, "emi"),
    ]);
    return { alb: albedo(alb, k), nrm, hgt, emi, mean: null };
  };
  try {
    const [grass, dirt, cobble, rock, soil] = await Promise.all(AllSlots.map(set));
    const base = { grass, dirt, cobble, rock, soil };
    // The purpose-made gorge wall (fractured slabs standing in earth, Phase 3 §6). Optional: a set that does not ship
    // one walls its chasms with the rock map as before (the same MapSet object).
    // Only its colour and normal are read (the cliff's roughness is a uniform), so only those two load; the rest of
    // the set is the rock's. A borrowed gorge albedo is graded like the rock's.
    const gorge = await Promise.all([one("gorge_alb", true), tex("gorge_nrm", false)])
      .then(([alb, nrm]): MapSet => ({ ...base.rock, alb: albedo(alb, "rock"), nrm }))
      .catch(() => base.rock);
    const maps: Maps = { ...base, gorge };
    // a compressed albedo has no pixels to average: the encoder measured it from the PNG
    const mean = compressed?.manifest.grassMean[biome];
    maps.grass.mean = (maps.grass.alb as THREE.CompressedTexture).isCompressedTexture
      ? (mean ? new THREE.Color(mean[0], mean[1], mean[2]) : null)
      : meanColour(maps.grass.alb);
    return maps;
  } catch {
    return null;                                              // maps not shipped: the caller keeps the tile kit
  }
}

/** Where tools/ktx/encode-terrain.mjs writes the KTX2 twins of the terrain maps, and their manifest. */
const KtxTerrainBase = "./hq/ktx2/terrain/";
interface KtxTerrainManifest {
  /** Per biome, the map names (`grass_alb`) that have a twin. */
  readonly sets: Readonly<Record<string, readonly string[]>>;
  /** Per biome, the grass albedo's mean colour, linear RGB. */
  readonly grassMean: Readonly<Record<string, readonly [number, number, number]>>;
}
let ktxTerrain: Promise<{ loader: KTX2Loader; manifest: KtxTerrainManifest } | null> | null = null;
/**
 * The compressed terrain maps, when the device takes them: the models' KTX2 loader (null under `?webp`, on a GPU
 * without a compressed format, in a single-file build) and the twins' manifest, fetched once.
 */
function compressedTerrain(): Promise<{ loader: KTX2Loader; manifest: KtxTerrainManifest } | null> {
  const loader = AssetLibrary.compressed;
  if (!loader) return Promise.resolve(null);
  ktxTerrain ??= fetch(AssetLibrary.resolveInlined(`${KtxTerrainBase}manifest.json`))
    .then(async (r) => (r.ok ? { loader, manifest: (await r.json()) as KtxTerrainManifest } : null))
    .catch(() => null);
  return ktxTerrain;
}

/**
 * The average colour of an albedo map, in linear RGB, so the grass palette can map whatever set is shipped onto
 * the biome's target colour rather than assuming today's map. Drawn down to 16² on a canvas and averaged; null
 * where the image cannot be read back (the shader then leaves the map's colour alone).
 */
function meanColour(t: THREE.Texture): THREE.Color | null {
  try {
    const img = t.image as CanvasImageSource | undefined;
    if (!img || typeof document === "undefined") return null;
    const cv = document.createElement("canvas");
    cv.width = cv.height = 16;
    const g = cv.getContext("2d", { willReadFrequently: true });
    if (!g) return null;
    g.drawImage(img, 0, 0, 16, 16);
    const d = g.getImageData(0, 0, 16, 16).data;
    const toLin = (v: number) => { const c = v / 255; return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
    let r = 0, gg = 0, b = 0;
    for (let i = 0; i < d.length; i += 4) { r += toLin(d[i]); gg += toLin(d[i + 1]); b += toLin(d[i + 2]); }
    const n = d.length / 4;
    return r + gg + b > 0 ? new THREE.Color(r / n, gg / n, b / n) : null;
  } catch {
    return null;
  }
}
